import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Eye, Edit, Trash, Check, X } from "lucide-react";

const HeaderActions = ({ buttons, slideFrom = "left" /* 'left' | 'right' */ }) => {
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const dropdownRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRefs = useRef({});

  const toggleDropdown = (index) => {
    setOpenDropdownIndex((prev) => {
      const next = prev === index ? null : index;
      if (next !== null) {
        const rect = btnRefs.current[next]?.getBoundingClientRect();
        if (rect) {
           setMenuPos({
          top: rect.bottom + 5,
          left: rect.left - 12,
          width: rect.width,
        });
        }
      }
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (event.target.closest('[data-ha-menu="true"]')) return;
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const IconComponent = ({ name }) => {
    const icons = { Eye, Edit, Trash, Check, X };
    const Icon = icons[name];
    return Icon ? <Icon size={16} className="shrink-0" /> : null;
  };

  const getHoverTextClass = (color) => {
    switch (color) {
      case "blue":
        return "hover:text-blue-600 dark:hover:text-blue-400";
      case "green":
        return "hover:text-green-600 dark:hover:text-green-400";
      case "red":
        return "hover:text-red-600 dark:hover:text-red-400";
      case "gray":
        return "hover:text-gray-600 dark:hover:text-gray-300";
      default:
        return "hover:text-gray-600 dark:hover:text-gray-300";
    }
  };

  const enterClass = slideFrom === "right" ? "ha-enter-right" : "ha-enter-left";

  return (
    <div className="grid grid-cols-2 gap-fluid-1 relative" ref={dropdownRef}>
      <style>{`
        @keyframes ha-in-left { from { opacity: 0; transform: translateX(-10px) } to { opacity: 1; transform: translateX(0) } }
        @keyframes ha-in-right { from { opacity: 0; transform: translateX(10px) } to { opacity: 1; transform: translateX(0) } }
        .ha-enter-left  { animation: ha-in-left  220ms ease-out both; }
        .ha-enter-right { animation: ha-in-right 220ms ease-out both; }
        .ha-hover { transition: transform 160ms ease; }
        .ha-hover:hover { transform: translateX(2px); }
        .ha-dropdown { animation: ha-in-right 200ms ease-out both; }
      `}</style>

      {buttons.length === 1 && <div></div>}

      {buttons.map(
        ({ label, onClick, type, color, disabled, icon, options, className }, index) => {
          const baseClasses =
            "min-w-[clamp(8rem,8vw,10rem)] px-fluid-2 py-fluid-1.5 rounded-full text-fluid-sm font-semibold transition-all duration-200 text-center ha-hover";

         const colorClasses = {
                    blue:
                      "text-[#223688] dark:text-[#9FB4FF] border border-[#223688] dark:border-[#3B5CCC] hover:bg-[#F5F6FC] dark:hover:bg-[#223688]/20",
                    gray:
                      "text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-acs-dark-border hover:bg-gray-50 dark:hover:bg-acs-dark-hover",
                    red:
                      "text-red-600 dark:text-red-300 border border-red-600 dark:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/40",
                    default:
                      "text-[#223688] dark:text-[#9FB4FF] border border-[#223688] dark:border-[#3B5CCC] hover:bg-[#F5F6FC] dark:hover:bg-[#223688]/20",
                  };



          const disabledClasses =
            "bg-[#F3F4F6] dark:bg-acs-dark-surface-2 text-[#A0A0A0] border border-[#C4C4C4] dark:border-acs-dark-border cursor-not-allowed";

          // Primary buttons remain identical in dark & light mode
          const primaryColorClasses = {
                    blue:
                      "bg-[#223688] text-white hover:bg-[#1A2B6D] focus:ring-2 focus:ring-[#223688] dark:bg-[#223688] dark:text-white dark:hover:bg-[#1A2B6D]",
                    red:
                      "bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-600 dark:bg-red-600 dark:text-white dark:hover:bg-red-700",
                    green:
                      "bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-600 dark:bg-green-600 dark:text-white dark:hover:bg-green-700",
                    default:
                      "bg-[#223688] text-white hover:bg-[#1A2B6D] focus:ring-2 focus:ring-[#223688] dark:bg-[#223688] dark:text-white dark:hover:bg-[#1A2B6D]",
                  };


          const buttonClasses = disabled
            ? `${baseClasses} ${disabledClasses}`
            : type === "primary"
            ? `${baseClasses} ${
                primaryColorClasses[color] || primaryColorClasses.default
              }`
            : `${baseClasses} ${
                colorClasses[color] || colorClasses.default
              }`;

          const staggerStyle = { animationDelay: `${index * 40}ms` };

          if (type === "dropdown") {
            const isOpen = openDropdownIndex === index;
            return (
              <div
                className={`relative ${className || ""} ${enterClass}`}
                key={index}
                style={staggerStyle}
              >
                <button
                  ref={(el) => (btnRefs.current[index] = el)}
                  disabled={disabled}
                  className={
                    buttonClasses +
                    " flex items-center justify-center gap-fluid-1"
                  }
                  onClick={() => !disabled && toggleDropdown(index)}
                >
                  {icon}
                  <span>{label}</span>
                  <ChevronDown
                    className={`transition-transform duration-300 ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                    size={16}
                  />
                </button>

                {isOpen &&
                  createPortal(
                    <div
                      data-ha-menu="true"
                      className="ha-dropdown fixed z-[9999] mix-blend-normal 
                                 border border-gray-200 dark:border-acs-dark-border 
                                 rounded-md shadow-lg 
                                 bg-white dark:bg-acs-dark-surface"
                      style={{
                        top: menuPos.top,
                        left: menuPos.left,
                        minWidth: Math.max(160, menuPos.width),
                      }}
                    >
                      <div className="flex flex-col p-fluid-2">
                        {options.map((opt, optIndex) => (
                          <button
                            key={optIndex}
                            className={`flex items-center gap-fluid-1 px-fluid-3 py-fluid-1.5
                              text-gray-600 dark:text-acs-dark-text
                              text-fluid-xs sm:text-fluid-sm
                              hover:bg-gray-50 dark:hover:bg-[#1f222a]
                              transition-colors rounded-md ${getHoverTextClass(opt.color)}
                              ${opt.muted ? "opacity-50 cursor-not-allowed" : ""}`}
                            onClick={() => {
                              setOpenDropdownIndex(null);
                              opt.onClick();
                            }}
                            disabled={opt.disabled}
                            title={opt.tooltip}
                          >
                            {typeof opt.icon === "string" ? (
                              <IconComponent name={opt.icon} />
                            ) : (
                              opt.icon && (
                                <span className="shrink-0">{opt.icon}</span>
                              )
                            )}
                            <span className="whitespace-nowrap">
                              {opt.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>,
                    document.body
                  )}
              </div>
            );
          }

          return (
            <button
              key={index}
              onClick={!disabled ? onClick : undefined}
              disabled={disabled}
              className={`${buttonClasses} ${enterClass}`}
              style={staggerStyle}
            >
              <span className="inline-flex items-center gap-fluid-1 justify-center">
                {icon}
                <span>{label}</span>
              </span>
            </button>
          );
        }
      )}
    </div>
  );
};

export default HeaderActions;
